import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, IconButton, Stack, Box } from '@mui/material';
import { keyframes } from '@mui/system';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TimerIcon from '@mui/icons-material/Timer';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';

const flashAnimation = keyframes`
  0% { background-color: rgba(211, 47, 47, 0); }
  25% { background-color: rgba(211, 47, 47, 0.4); }
  50% { background-color: rgba(211, 47, 47, 0.1); }
  75% { background-color: rgba(211, 47, 47, 0.4); }
  100% { background-color: rgba(211, 47, 47, 0.15); }
`;

const TimerWidget = ({ defaultTime = 90 }) => {
  const [presetDuration, setPresetDuration] = useState(defaultTime * 1000);
  const [remainingTime, setRemainingTime] = useState(defaultTime * 1000);
  const [isRunning, setIsRunning] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    const saved = localStorage.getItem('timerAlertsEnabled');
    return saved === 'true';
  });

  const endTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const hasAlertedRef = useRef(false);
  const audioContextRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const playBeep = useCallback(() => {
    try {
      const audioContext = getAudioContext();

      [0, 0.25, 0.5].forEach((delay) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        const startTime = audioContext.currentTime + delay;
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.15);

        oscillator.start(startTime);
        oscillator.stop(startTime + 0.15);
      });
    } catch (err) {
      console.error('Audio playback failed:', err);
    }
  }, [getAudioContext]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('timerAlertsEnabled', alertsEnabled.toString());
  }, [alertsEnabled]);

  const toggleAlerts = () => {
    if (!alertsEnabled) {
      getAudioContext();
      if ('vibrate' in navigator) {
        navigator.vibrate(1);
      }
    }
    setAlertsEnabled(prev => !prev);
  };

  const requestWakeLock = async () => {
    if (!('wakeLock' in navigator)) return;

    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch (err) {
      console.log('Wake Lock request failed:', err);
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isRunning) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning]);

  const triggerAlert = useCallback(() => {
    if (hasAlertedRef.current) return;
    if (!alertsEnabled) return;

    hasAlertedRef.current = true;

    playBeep();

    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200, 100, 200, 100, 200]);
    }
  }, [alertsEnabled, playBeep]);

  const formatTime = (ms) => {
    const isNegative = ms < 0;
    const absMs = Math.abs(ms);
    const totalSeconds = Math.ceil(absMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const displaySeconds = seconds.toString().padStart(2, '0');
    return `${isNegative ? '-' : ''}${displayMinutes}:${displaySeconds}`;
  };

  const clearTimerInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const tick = useCallback(() => {
    if (!endTimeRef.current) return;
    const now = Date.now();
    const left = endTimeRef.current - now;
    setRemainingTime(left);

    if (left <= 0 && !hasAlertedRef.current) {
      triggerAlert();
    }
  }, [triggerAlert]);

  const startTimer = useCallback(async () => {
    if (isRunning) return;

    if (alertsEnabled) {
      getAudioContext();
      if ('vibrate' in navigator) {
        navigator.vibrate(1);
      }
    }

    hasAlertedRef.current = false;
    const now = Date.now();
    endTimeRef.current = now + remainingTime;
    setIsRunning(true);
    intervalRef.current = setInterval(tick, 100);

    await requestWakeLock();
  }, [isRunning, remainingTime, tick, getAudioContext, alertsEnabled]);

  const stopTimer = useCallback(() => {
    if (!isRunning) return;
    clearTimerInterval();
    setIsRunning(false);
    if (endTimeRef.current) {
      setRemainingTime(endTimeRef.current - Date.now());
    }
    endTimeRef.current = null;
    releaseWakeLock();
  }, [isRunning]);

  const resetTimer = useCallback(() => {
    clearTimerInterval();
    setIsRunning(false);
    endTimeRef.current = null;
    setRemainingTime(presetDuration);
    hasAlertedRef.current = false;
    releaseWakeLock();
  }, [presetDuration]);

  const restartTimer = useCallback(async () => {
    clearTimerInterval();

    if (alertsEnabled) {
      getAudioContext();
      if ('vibrate' in navigator) {
        navigator.vibrate(1);
      }
    }

    hasAlertedRef.current = false;

    const now = Date.now();
    endTimeRef.current = now + presetDuration;
    setRemainingTime(presetDuration);
    setIsRunning(true);
    intervalRef.current = setInterval(tick, 100);

    await requestWakeLock();
  }, [presetDuration, tick, getAudioContext, alertsEnabled]);

  const adjustTime = useCallback((amountMs) => {
    const isSetupMode = !isRunning && remainingTime === presetDuration;

    if (isSetupMode) {
      const newDuration = Math.max(0, presetDuration + amountMs);
      setPresetDuration(newDuration);
      setRemainingTime(newDuration);
    } else {
      if (isRunning) {
        endTimeRef.current += amountMs;
        tick();
      } else {
        setRemainingTime(prev => prev + amountMs);
      }
    }

    if (remainingTime + amountMs > 0) {
      hasAlertedRef.current = false;
    }
  }, [isRunning, remainingTime, presetDuration, tick]);

  useEffect(() => {
    return () => {
      clearTimerInterval();
      releaseWakeLock();
    };
  }, []);

  const isOverdue = remainingTime <= 0;
  const isDanger = remainingTime <= 10000 && remainingTime > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.5,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 'auto',
        borderRadius: 2,
        border: 1,
        borderColor: isOverdue ? 'error.main' : 'divider',
        boxShadow: 1,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        bgcolor: isOverdue ? 'rgba(211, 47, 47, 0.15)' : 'background.paper',
        animation: isOverdue && isRunning ? `${flashAnimation} 2s ease-in-out infinite` : 'none',
      }}
    >
      <IconButton
        size="small"
        onClick={toggleAlerts}
        color={alertsEnabled ? 'primary' : 'default'}
        sx={{ p: 0.5 }}
        title={alertsEnabled ? 'Alerts enabled' : 'Alerts disabled'}
      >
        {alertsEnabled ? (
          <NotificationsActiveIcon fontSize="small" />
        ) : (
          <NotificationsOffIcon fontSize="small" />
        )}
      </IconButton>

      <Box sx={{ width: 1, height: 24, bgcolor: 'divider' }} />

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" onClick={() => adjustTime(15000)} sx={{ p: 0.5 }} title="+15s">
          <AddIcon fontSize="small" />
        </IconButton>

        <TimerIcon
          fontSize="small"
          color={isOverdue ? "error" : (isRunning ? "primary" : "action")}
          sx={{ transition: 'color 0.3s' }}
        />

        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'monospace',
            fontSize: '1rem',
            minWidth: '52px',
            color: isOverdue ? 'error.main' : (isDanger ? 'warning.main' : 'text.primary'),
            transition: 'color 0.3s',
            mx: 0.5,
          }}
        >
          {formatTime(remainingTime)}
        </Typography>

        <IconButton size="small" onClick={() => adjustTime(-15000)} sx={{ p: 0.5 }} title="-15s">
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ width: 1, height: 24, bgcolor: 'divider' }} />

      <Stack direction="row" spacing={0} alignItems="center">
        {!isRunning ? (
          <IconButton size="small" onClick={startTimer} color="primary" sx={{ padding: 0.5 }} title="Start">
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={stopTimer} color="warning" sx={{ padding: 0.5 }} title="Pause">
            <PauseIcon fontSize="small" />
          </IconButton>
        )}

        <IconButton size="small" onClick={restartTimer} sx={{ padding: 0.5 }} title="Restart">
          <AutorenewIcon fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={resetTimer}
          disabled={!isRunning && remainingTime === presetDuration}
          sx={{ padding: 0.5 }}
          title="Reset"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

TimerWidget.propTypes = {
  defaultTime: PropTypes.number,
};

export default TimerWidget;